// src/hooks/useTaxonomyTerms.ts
// Hook for fetching Drupal taxonomy terms via the EM3 backend proxy.
// The backend route (/api/taxonomy/:vocabulary) hits CMS server-side
// with the CMS service-account api-key, so this dropdown no longer
// depends on the user's CMS session cookie or an FA-issued bearer.

import { useState, useEffect, useCallback } from 'react';

export interface TaxonomyTerm {
  id: string;         // UUID (use this for relationships)
  internalId: number; // Drupal internal ID
  name: string;
  allowedExtensions?: string[]; // e.g. ['pdf','jpg'] — from field_allowed_extensions on subcategory terms
}

export function useTaxonomyTerms(vocabulary: string) {
  const [terms, setTerms] = useState<TaxonomyTerm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchTerms = useCallback(async () => {
    // Empty vocabulary (content type without a subcategory) — skip the
    // fetch and stay empty without spinning.
    if (!vocabulary) {
      setTerms([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `/api/taxonomy/${encodeURIComponent(vocabulary)}`,
        {
          headers: { 'Accept': 'application/vnd.api+json' },
          credentials: 'include',
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch ${vocabulary} (HTTP ${response.status})`);
      }

      const data = await response.json();
      setTerms(data.data.map((term: Record<string, unknown>) => {
        const attrs = term.attributes as Record<string, unknown>;
        return {
          id: term.id as string,
          internalId: attrs.drupal_internal__tid as number,
          name: attrs.name as string,
          allowedExtensions: Array.isArray(attrs.field_allowed_extensions)
            ? (attrs.field_allowed_extensions as string[])
            : undefined,
        };
      }));
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [vocabulary]);

  useEffect(() => {
    fetchTerms();
  }, [fetchTerms]);

  return { terms, loading, error, refetch: fetchTerms };
}
